// store/modules/im/im.js
const state = {
    imInstans: null,
    isLogin: false,
    userGuid: '',
    friendslist: [],
    friendsGuid: [],
    sessionMap: new Map(),
    currentSessionGuid: '',
    globalUnreadCount: 0,
    friendsPagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0,
        hasMore: true,
        loading: false
    },
    allFriendsLoaded: false,
    // 添加更新标记
    _updateTimestamp: 0
};

const mutations = {
    SET_IM_INSTANCE(state, instance) { state.imInstans = instance; },
    SET_LOGIN(state, isLogin) { state.isLogin = isLogin; },
    SET_USER_GUID(state, guid) { state.userGuid = guid; },

    SET_FRIENDS_LIST(state, list) {
        if (!Array.isArray(list)) list = [];
        const uniqueMap = new Map();
        list.forEach(friend => {
            if (friend?.agentAccountGuid) uniqueMap.set(friend.agentAccountGuid, friend);
        });
        state.friendslist = Array.from(uniqueMap.values());
        state.friendsGuid = state.friendslist.map(f => f.agentAccountGuid);
        state._updateTimestamp = Date.now();
    },

    SET_FRIENDS_GUID(state, guids) { state.friendsGuid = guids; },

    SET_SESSION_MAP(state, sessionMap) {
        if (!(sessionMap instanceof Map)) {
            console.warn('[IM] SET_SESSION_MAP: 参数不是 Map 实例');
            return;
        }

        // 标准化 session 数据结构
        const normalizedMap = new Map();
        for (const [key, value] of sessionMap) {
            if (!value || typeof value !== 'object') {
                normalizedMap.set(key, { messages: [], unreadCount: 0, lastMsg: null, lastMsgTime: null });
            } else if (Array.isArray(value)) {
                normalizedMap.set(key, {
                    messages: value,
                    unreadCount: 0,
                    lastMsg: value[value.length - 1] || null,
                    lastMsgTime: value[value.length - 1]?.time || null
                });
            } else if (!value.messages) {
                normalizedMap.set(key, {
                    messages: value.messages || [],
                    unreadCount: value.unreadCount || 0,
                    lastMsg: value.lastMsg || null,
                    lastMsgTime: value.lastMsgTime || null,
                    ...value
                });
            } else {
                normalizedMap.set(key, { ...value });
            }
        }

        // 替换整个 Map 以确保响应式
        state.sessionMap = normalizedMap;
        state._updateTimestamp = Date.now();
    },

    UPDATE_SESSION_MESSAGES(state, { sessionId, messages }) {
        if (!(state.sessionMap instanceof Map)) {
            state.sessionMap = new Map();
        }

        let session = state.sessionMap.get(sessionId);
        if (!session) {
            session = { messages: [], unreadCount: 0, lastMsg: null, lastMsgTime: null };
        }

        const msgArray = Array.isArray(messages) ? [...messages] : [];
        msgArray.sort((a, b) => (a.time || 0) - (b.time || 0));

        session.messages = msgArray;
        if (msgArray.length) {
            session.lastMsg = msgArray[msgArray.length - 1];
            session.lastMsgTime = session.lastMsg.time;
        }

        // 重新设置以触发响应式
        state.sessionMap = new Map(state.sessionMap);
        state.sessionMap.set(sessionId, session);
        state._updateTimestamp = Date.now();
    },

    ADD_MESSAGE_TO_SESSION(state, { sessionId, message }) {
        if (!(state.sessionMap instanceof Map)) {
            state.sessionMap = new Map();
        }

        let session = state.sessionMap.get(sessionId);
        if (!session) {
            session = { messages: [], unreadCount: 0, lastMsg: null, lastMsgTime: null };
            state.sessionMap.set(sessionId, session);
        }

        if (!session.messages) session.messages = [];

        const exists = session.messages.some(m => m?.guid === message.guid);
        if (!exists) {
            session.messages.push(message);
            session.messages.sort((a, b) => (a.time || 0) - (b.time || 0));
            session.lastMsg = message;
            session.lastMsgTime = message.time;

            if (state.currentSessionGuid !== sessionId && message.fromGuid !== state.userGuid) {
                session.unreadCount = (session.unreadCount || 0) + 1;
            }

            // 强制触发响应式更新
            state.sessionMap = new Map(state.sessionMap);
            state._updateTimestamp = Date.now();
        }
    },

    ADD_MESSAGES_TO_SESSION(state, { sessionId, messages, prepend = false }) {
        if (!(state.sessionMap instanceof Map)) {
            state.sessionMap = new Map()
        }

        let session = state.sessionMap.get(sessionId)
        if (!session) {
            session = {
                messages: [],
                unreadCount: 0,
                lastMsg: null,
                lastMsgTime: null,
                friendInfo: null
            }
            state.sessionMap.set(sessionId, session)
        }

        if (!session.messages) session.messages = []

        const msgArray = Array.isArray(messages) ? messages : []
        if (!msgArray.length) return

        // 去重
        const existingGuids = new Set(session.messages.map(m => m?.guid).filter(Boolean))
        const newMessages = msgArray.filter(m => m?.guid && !existingGuids.has(m.guid))

        if (!newMessages.length) return

        if (prepend) {
            session.messages = [...newMessages, ...session.messages]
        } else {
            session.messages = [...session.messages, ...newMessages]
        }

        // 排序
        session.messages.sort((a, b) => (a.time || 0) - (b.time || 0))

        // 更新最后一条消息
        if (session.messages.length) {
            session.lastMsg = session.messages[session.messages.length - 1]
            session.lastMsgTime = session.lastMsg.time
        }

        // 更新未读数（非当前会话且不是自己发的消息）
        if (state.currentSessionGuid !== sessionId && !prepend) {
            const unreadIncrease = newMessages.filter(m => m.fromGuid !== state.userGuid).length
            if (unreadIncrease) {
                session.unreadCount = (session.unreadCount || 0) + unreadIncrease
            }
        }

        // 强制触发响应式更新
        state.sessionMap = new Map(state.sessionMap)
        state._updateTimestamp = Date.now()
    },

    SET_CURRENT_SESSION_GUID(state, guid) {
        state.currentSessionGuid = guid;
        state._updateTimestamp = Date.now();
    },

    UPDATE_UNREAD_COUNT(state, { sessionId, unreadCount }) {
        if (!(state.sessionMap instanceof Map)) return;

        const session = state.sessionMap.get(sessionId);
        if (session) {
            session.unreadCount = unreadCount;
            // 强制触发响应式更新
            state.sessionMap = new Map(state.sessionMap);
            state._updateTimestamp = Date.now();
        }
    },

    SET_GLOBAL_UNREAD(state, count) { state.globalUnreadCount = count; },
    SET_FRIENDS_PAGINATION(state, pagination) {
        state.friendsPagination = { ...state.friendsPagination, ...pagination };
    },
    SET_ALL_FRIENDS_LOADED(state, loaded) { state.allFriendsLoaded = loaded; },
    FORCE_REFRESH_CONVERSATIONS(state) {
        state._updateTimestamp = Date.now();
    },
};

const getters = {
    hasFriends: (state) => state.friendslist?.length > 0,
    totalUnreadCount: (state) => {
        let total = 0;
        if (state.sessionMap instanceof Map) {
            for (const [_, session] of state.sessionMap) {
                total += session?.unreadCount || 0;
            }
        }
        return total;
    }
};

export { state, mutations, getters };